import { useMemo, useCallback, type ReactNode } from 'react';
import { Button, IconButton } from '@carbon/react';
import { Add, Close, Copy, Edit } from '@carbon/react/icons';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  ColorByAttributeModal,
  type ColorByRule,
  type ColorByCategory,
  type ColorByCategoryGroup,
  type ColorByOption,
} from 'components/color-by-attribute-modal';
import {
  ColorSwatch,
  DEFAULT_COLORS,
  hexToRgba,
} from 'components/color-picker';
import { SortableList } from 'components/sortable-list';
import { useDatasetConfig } from './dataset-context';
import { orderFacetItems } from './filter-modal/facet-label';
import { reorderWithinGroup } from './color-by-utils';
import { datasetRegistry } from './datasets';
import {
  allDatasetAtoms,
  mapQueryColorRulesAtom,
  colorByModalOpenAtom,
  colorByEditingRuleIdAtom,
  colorByInitialGroupIdAtom,
  noFacetsAtom,
} from './atoms';
import { useSubsurfaceColorGroup } from './subsurface-maps/hooks';
import { FLOATING_MENU_SELECTORS } from './subsurface-maps/ramp-editor-popover';
import styles from './color-by-section.module.scss';

// Cross-dataset attributes — surfaced once on the Common tab instead of
// repeating under each dataset's own tab. Single source for both the Common
// tab's categories and the keys stripped from the per-dataset tabs.
const COMMON_ATTRIBUTES: ColorByCategory[] = [
  { id: 'currentOperator', label: 'Operator' },
  { id: 'state', label: 'State' },
];
const COMMON_FACET_KEYS = COMMON_ATTRIBUTES.map((c) => c.id);

// A dataset's own tab lists exactly the attributes it can paint (its
// `colorByAttributes`), minus the Common ones. Deriving from the config keeps
// the modal from drifting: only attributes wired to a tile/row field appear.
const datasetCategories = (datasetId: string): ColorByCategory[] =>
  (datasetRegistry[datasetId].colorByAttributes ?? [])
    .filter((attr) => !COMMON_FACET_KEYS.includes(attr.facetKey))
    .map((attr) => ({ id: attr.facetKey, label: attr.label }));

// Tabs for the global Color-by-Attribute modal. Category ids are facet keys so
// value options resolve straight from each dataset's search facets. The Common
// tab is curated (cross-dataset attributes); per-dataset tabs are derived from
// the dataset configs.
const COLOR_GROUPS: ColorByCategoryGroup[] = [
  {
    id: 'common',
    label: 'Common',
    appliesToLabel: 'Applies to Wells + Documents',
    categories: COMMON_ATTRIBUTES,
  },
  {
    id: 'wells',
    label: 'Wells',
    appliesToLabel: 'Applies to Wells',
    categories: datasetCategories('wells'),
  },
  {
    id: 'documents',
    label: 'Documents',
    appliesToLabel: 'Applies to Documents',
    categories: datasetCategories('documents'),
  },
  // Areas paint only while their dataset is registered.
  ...(datasetRegistry.areas
    ? [
        {
          id: 'areas',
          label: 'Areas',
          appliesToLabel: 'Applies to Areas',
          categories: datasetCategories('areas'),
        },
      ]
    : []),
];

// facetKey -> display label, for the legends (across every tab).
const LABEL_BY_FACET: Record<string, string> = Object.fromEntries(
  COLOR_GROUPS.flatMap((g) => g.categories.map((c) => [c.id, c.label]))
);

const labelForAttribute = (attribute: string): string =>
  LABEL_BY_FACET[attribute] ?? attribute;

const NEW_RULE_APPLY_TO = { map: true, table: true };

// Which dataset's search facets back each tab's value lists.
const FACET_SOURCE_BY_GROUP: Record<string, string> = {
  common: 'wells',
  wells: 'wells',
  documents: 'documents',
  areas: 'areas',
};

// A boolean facet (Areas' Shared) names its buckets true / false, not strings.
type FacetBucket = { id: string | number; name: string | number | boolean };
type FacetMap = Record<string, { buckets?: FacetBucket[] }>;

// ─── Rule legend (swatches + value labels) ───

interface LegendProps {
  rules: ColorByRule[];
  onRemove: (ruleId: string) => void;
  onEdit: (ruleId: string) => void;
  onToggleEnabled: (ruleId: string) => void;
  // Click a value swatch → color picker → update that value's color.
  onColorChange: (ruleId: string, value: string, color: string) => void;
  // Drag-reorder → new order of this legend's rules (highest precedence last).
  onReorder: (rules: ColorByRule[]) => void;
  /**
   * Display names for attributes this legend's own map does not know. Map Query's map
   * covers its own facets; a consumer colouring by anything else (the well rails) would
   * otherwise see the raw attribute id as the rule's name.
   */
  attributeLabels?: Record<string, string>;
  /**
   * Extra line under a rule's name — the well rails use it for the mock's
   * "Active on: Map, 3D View". Omitted by Map Query, which hides Apply To entirely and
   * so has no surfaces to name.
   */
  renderRuleMeta?: (rule: ColorByRule) => ReactNode;
  /**
   * Copy a rule. Omitted by Map Query, which needs no duplicate: it hides Apply To, so
   * every rule already paints every surface and a copy would only ever be redundant.
   */
  onDuplicate?: (ruleId: string) => void;
  /**
   * How the per-rule controls sit. `inline` (default) is Map Query's single row.
   * `stacked` is mock v13's wells layout: Remove beside the name, the rest on their own
   * row under "Active on" — which is where the extra controls fit once a rule also
   * carries a surface list.
   */
  actionsLayout?: 'inline' | 'stacked';
}

// New rule list with one value's color replaced (immutable).
function withUpdatedColor(
  rules: ColorByRule[],
  ruleId: string,
  value: string,
  color: string
): ColorByRule[] {
  return rules.map((r) => {
    if (r.ruleId !== ruleId) return r;
    return { ...r, valueColors: { ...r.valueColors, [value]: color } };
  });
}

// New rule list with one rule's enabled flag flipped (immutable). A disabled
// rule stays listed but stops painting (both map + card painters skip it).
function withToggledEnabled(
  rules: ColorByRule[],
  ruleId: string
): ColorByRule[] {
  return rules.map((r) => {
    if (r.ruleId !== ruleId) return r;
    return { ...r, enabled: !r.enabled };
  });
}

// Exported so the Well page and Well Workspace rails render the same colour-rule legend.
export const ColorRulesLegend = ({
  rules,
  onRemove,
  onEdit,
  onToggleEnabled,
  onColorChange,
  onReorder,
  attributeLabels,
  renderRuleMeta,
  onDuplicate,
  actionsLayout = 'inline',
}: LegendProps) => {
  const stacked = actionsLayout === 'stacked';

  return (
    <div className={styles.rules} data-color-legend>
      {rules.length > 1 && (
        <p className={styles.reorderHint}>
          Drag to reorder — objects that match several rules take the color of
          the rule listed last.
        </p>
      )}
      <SortableList
        items={rules}
        getItemKey={(rule) => rule.ruleId}
        onReorder={onReorder}
        renderItem={(rule) => (
          <div className={styles.rule}>
            <div className={styles.ruleHeader}>
              <span
                className={classNames(styles.ruleName, {
                  [styles.ruleNameDisabled]: !rule.enabled,
                })}
              >
                {attributeLabels?.[rule.attribute] ??
                  labelForAttribute(rule.attribute)}
              </span>
              <div className={styles.ruleActions}>
                {/* Stacked keeps only Remove up here, beside the rule's name, and drops
                  the rest below the "Active on" line — mock v13's wells rails. Inline
                  is the row Map Query has always drawn. */}
                {stacked ? (
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="left"
                    autoAlign
                    label="Remove rule"
                    onClick={() => onRemove(rule.ruleId)}
                  >
                    <Close />
                  </IconButton>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.disableToggle}
                      onClick={() => onToggleEnabled(rule.ruleId)}
                    >
                      {rule.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <IconButton
                      kind="ghost"
                      size="sm"
                      align="left"
                      autoAlign
                      label="Edit rule"
                      onClick={() => onEdit(rule.ruleId)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      kind="ghost"
                      size="sm"
                      align="left"
                      autoAlign
                      label="Remove rule"
                      onClick={() => onRemove(rule.ruleId)}
                    >
                      <Close />
                    </IconButton>
                  </>
                )}
              </div>
            </div>
            {renderRuleMeta?.(rule)}
            {stacked && (
              <div className={styles.ruleActionsRow}>
                <IconButton
                  kind="ghost"
                  size="sm"
                  align="left"
                  autoAlign
                  label="Edit rule"
                  onClick={() => onEdit(rule.ruleId)}
                >
                  <Edit />
                </IconButton>
                {/* Copy a rule's attribute, values and colours, then retarget the copy.
                  Worth having precisely because a surface can only be claimed once:
                  without it, painting a second view means rebuilding the whole value
                  list and its colours by hand. */}
                {onDuplicate && (
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="left"
                    autoAlign
                    label="Duplicate rule"
                    onClick={() => onDuplicate(rule.ruleId)}
                  >
                    <Copy />
                  </IconButton>
                )}
                <button
                  type="button"
                  className={styles.disableToggle}
                  onClick={() => onToggleEnabled(rule.ruleId)}
                >
                  {rule.enabled ? 'Disable' : 'Enable'}
                </button>
              </div>
            )}
            <div className={styles.ruleValues}>
              {rule.selectedValues.map((value, i) => (
                <div key={value} className={styles.ruleValue}>
                  <ColorSwatch
                    color={
                      rule.valueColors[value] ??
                      DEFAULT_COLORS[i % DEFAULT_COLORS.length]
                    }
                    popoverPosition="auto"
                    onChange={(newColor, newOpacity) =>
                      onColorChange(
                        rule.ruleId,
                        value,
                        hexToRgba(newColor, newOpacity)
                      )
                    }
                  />
                  <span>{rule.selectedValueLabels?.[i] ?? value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      />
    </div>
  );
};

// ─── Global "Color by Attribute" control (tabbed: Common / Wells / Documents) ───

export const ColorBySection = () => {
  const [rules, setRules] = useAtom(mapQueryColorRulesAtom);
  const wellsFacets = useAtomValue(allDatasetAtoms.wells.committedFacetsAtom);
  const documentsFacets = useAtomValue(
    allDatasetAtoms.documents.committedFacetsAtom
  );
  // The registry is fixed at load, so this reads the same atom on every render.
  const areasFacets = useAtomValue(
    allDatasetAtoms.areas?.committedFacetsAtom ?? noFacetsAtom
  );
  const [open, setOpen] = useAtom(colorByModalOpenAtom);
  const [editingRuleId, setEditingRuleId] = useAtom(colorByEditingRuleIdAtom);
  const [initialGroupId, setInitialGroupId] = useAtom(
    colorByInitialGroupIdAtom
  );
  const subsurfaceGroup = useSubsurfaceColorGroup(open);
  const groups = useMemo(
    () => [...COLOR_GROUPS, subsurfaceGroup],
    [subsurfaceGroup]
  );

  const facetsBySource = useMemo<Record<string, FacetMap | undefined>>(
    () => ({
      wells: wellsFacets ?? undefined,
      documents: documentsFacets ?? undefined,
      areas: areasFacets ?? undefined,
    }),
    [wellsFacets, documentsFacets, areasFacets]
  );

  // Value options come from the tab's backing dataset facets, keyed by facetKey.
  const getOptions = useCallback(
    (categoryId: string, groupId?: string): ColorByOption[] => {
      const source = FACET_SOURCE_BY_GROUP[groupId ?? ''] ?? 'wells';
      // Common-tab rules paint in wells-space tokens, the values the painters match
      // on, so its options come from the wells facets. The documents `state` facet is
      // keyed by DB id, a different token space that only produced duplicate rows.
      const buckets = facetsBySource[source]?.[categoryId]?.buckets ?? [];
      const options = buckets.map((b) => ({
        id: String(b.id),
        // A string name passes through as before; Areas' Shared names its buckets
        // true / false, which the modal's search would call toLowerCase on.
        name: typeof b.name === 'string' ? b.name : String(b.name ?? b.id),
      }));
      // The Documents tab's value lists (types, subtypes, …) read alphabetically.
      return orderFacetItems(options, source === 'documents');
    },
    [facetsBySource]
  );

  const openModal = (ruleId: string | null) => {
    setEditingRuleId(ruleId);
    setInitialGroupId(null);
    setOpen(true);
  };

  // The global section lists the cross-dataset ("Common") rules; each dataset's
  // own rules render under its sidebar section (see DatasetColorRules).
  const commonRules = rules.filter((r) => r.groupId === 'common');

  return (
    <div className={styles.section}>
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Add}
        onClick={() => openModal(null)}
      >
        Color by Attribute
      </Button>

      {commonRules.length === 0 ? (
        <p className={styles.emptyText}>No color rules added.</p>
      ) : (
        <ColorRulesLegend
          rules={commonRules}
          onEdit={(id) => openModal(id)}
          onRemove={(id) => setRules((rs) => rs.filter((r) => r.ruleId !== id))}
          onToggleEnabled={(id) => setRules((rs) => withToggledEnabled(rs, id))}
          onColorChange={(ruleId, value, color) =>
            setRules((rs) => withUpdatedColor(rs, ruleId, value, color))
          }
          onReorder={(ordered) =>
            setRules((rs) => reorderWithinGroup(rs, 'common', ordered))
          }
        />
      )}

      <ColorByAttributeModal
        open={open}
        onClose={() => setOpen(false)}
        editingRuleId={editingRuleId}
        rules={rules}
        onApply={(next) => setRules(next)}
        categoryGroups={groups}
        initialGroupId={initialGroupId ?? undefined}
        selectorsFloatingMenus={FLOATING_MENU_SELECTORS}
        categorySearchPlaceholder="Search Attributes"
        getOptions={getOptions}
        newRuleApplyTo={NEW_RULE_APPLY_TO}
        showReset={false}
        showApplyTo={false}
        title="Color by Attribute"
      />
    </div>
  );
};

// ─── Per-dataset legend shown inside each dataset's sidebar section ───

export const DatasetColorRules = () => {
  const config = useDatasetConfig();
  const [rules, setRules] = useAtom(mapQueryColorRulesAtom);
  const setModalOpen = useSetAtom(colorByModalOpenAtom);
  const setEditingRuleId = useSetAtom(colorByEditingRuleIdAtom);

  const ownRules = rules.filter((r) => r.groupId === config.id);
  if (ownRules.length === 0) {
    return <p className={styles.emptyText}>No color rules added.</p>;
  }
  return (
    <ColorRulesLegend
      rules={ownRules}
      onEdit={(id) => {
        setEditingRuleId(id);
        setModalOpen(true);
      }}
      onRemove={(id) => setRules((rs) => rs.filter((r) => r.ruleId !== id))}
      onToggleEnabled={(id) => setRules((rs) => withToggledEnabled(rs, id))}
      onColorChange={(ruleId, value, color) =>
        setRules((rs) => withUpdatedColor(rs, ruleId, value, color))
      }
      onReorder={(ordered) =>
        setRules((rs) => reorderWithinGroup(rs, config.id, ordered))
      }
    />
  );
};
